import React, { useId } from 'react';

// Tiny inline trend line with a gradient fill. Ported from splitfin-manager
// src/components/dash/sparkline.tsx; the draw-on animation is a CSS
// stroke-dash transition instead of motion.
export const Sparkline: React.FC<{ points: number[]; color: string; className?: string; area?: boolean; height?: number }> = ({
  points,
  color,
  className,
  area = true,
  height = 40,
}) => {
  const id = useId().replace(/:/g, '');
  const w = 120;
  const h = height;
  if (points.length < 2) return <div className={className} style={{ height }} />;

  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const coords = points.map((p, i) => [i * step, h - 3 - ((p - min) / span) * (h - 6)] as const);

  // Catmull-Rom → cubic Bézier for a soft curve.
  let d = `M ${coords[0][0]} ${coords[0][1]}`;
  for (let i = 0; i < coords.length - 1; i++) {
    const p0 = coords[Math.max(i - 1, 0)];
    const p1 = coords[i];
    const p2 = coords[i + 1];
    const p3 = coords[Math.min(i + 2, coords.length - 1)];
    d += ` C ${p1[0] + (p2[0] - p0[0]) / 6} ${p1[1] + (p2[1] - p0[1]) / 6}, ${p2[0] - (p3[0] - p1[0]) / 6} ${p2[1] - (p3[1] - p1[1]) / 6}, ${p2[0]} ${p2[1]}`;
  }
  const last = coords[coords.length - 1];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={className} style={{ height }} aria-hidden>
      <defs>
        <linearGradient id={`${id}-fill`} x1="0" x2="0" y1="0" y2="1">
          {/* Colours go through style: `color` may be a CSS variable, which presentation attributes ignore. */}
          <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.25 }} />
          <stop offset="100%" style={{ stopColor: color, stopOpacity: 0 }} />
        </linearGradient>
      </defs>
      {area ? <path d={`${d} L ${w} ${h} L 0 ${h} Z`} fill={`url(#${id}-fill)`} /> : null}
      <path d={d} fill="none" style={{ stroke: color }} strokeWidth={1.75} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      <circle cx={last[0]} cy={last[1]} r={2.4} style={{ fill: color }} />
    </svg>
  );
};
